// src/store/systemSettingsStore.ts
import { create } from 'zustand';
import { adminService } from '../services/admin';
import api from '../services/api';

interface SystemSettings {
  app_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  footer_text: string;
  app_description: string;
  institution_name: string;
  institution_address: string;
  institution_phone: string;
  institution_email: string;
  institution_website: string;
  institution_code: string;
  login_background: string | null;
  primary_color: string;
  // SLA Settings
  sla_rendah: number;
  sla_sedang: number;
  sla_tinggi: number;
  sla_critical: number;
  [key: string]: any;
}

interface SystemSettingsState {
  settings: SystemSettings;
  isLoading: boolean;
  isHydrated: boolean;
  fetchSettings: () => Promise<void>;
  fetchPublicSettings: () => Promise<void>;
  updateSettings: (newSettings: Partial<SystemSettings>) => void;
}

const DEFAULT_SETTINGS: SystemSettings = {
  app_name: 'SIM-EVAL',
  app_description: 'Sistem Informasi Manajemen Evaluasi',
  institution_name: 'Universitas Buana Perjuangan Karawang',
  institution_address: '',
  institution_phone: '',
  institution_email: '',
  institution_website: '',
  institution_code: 'UBP-KRW',
  logo_url: null,
  favicon_url: null,
  footer_text: '© 2026 SIM-EVAL UBP Karawang. All rights reserved.',
  primary_color: '#0f33ab',
  login_background: null,
  // SLA Defaults
  sla_rendah: 60,
  sla_sedang: 30,
  sla_tinggi: 14,
  sla_critical: 7,
};

const getStoredSettings = (): SystemSettings => {
  try {
    const stored = localStorage.getItem('system_settings');
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to parse stored settings:', e);
  }
  return DEFAULT_SETTINGS;
};

export const useSystemSettingsStore = create<SystemSettingsState>((set, get) => ({
  settings: getStoredSettings(),
  isLoading: false,
  isHydrated: !!localStorage.getItem('system_settings'),

  fetchSettings: async () => {
    if (get().isLoading) return;

    set({ isLoading: true });

    try {
      const res = await adminService.settings.getAll();

      const newSettings: SystemSettings = { ...DEFAULT_SETTINGS };

      res.forEach((item: any) => {
        if (item.type === 'boolean') {
          newSettings[item.key] = item.value === '1' || item.value === 'true';
        } else if (item.type === 'number' || item.type === 'int') {
          newSettings[item.key] = Number(item.value);
        } else {
          newSettings[item.key] = item.value;
        }
      });

      localStorage.setItem('system_settings', JSON.stringify(newSettings));

      set({
        settings: newSettings,
        isLoading: false,
        isHydrated: true
      });
    } catch (e) {
      console.error('Failed to fetch settings:', e);
      set({ isLoading: false });
    }
  },

  fetchPublicSettings: async () => {
    // Skip if already loading
    if (get().isLoading) return;

    // We still fetch even if hydrated to ensure we have the latest data from server
    set({ isLoading: true });
    try {
      const response = await api.get('/public/settings');
      const res = response.data?.data || response.data || {};
 
       // Convert SLA values to numbers
       const slaKeys = ['sla_rendah', 'sla_sedang', 'sla_tinggi', 'sla_critical'];
       slaKeys.forEach(key => {
         if (res[key] !== undefined) {
           res[key] = parseInt(res[key], 10) || DEFAULT_SETTINGS[key as keyof SystemSettings];
         }
       });
 
       const updatedSettings = { ...get().settings, ...res };

      localStorage.setItem('system_settings', JSON.stringify(updatedSettings));
      set({ settings: updatedSettings, isLoading: false, isHydrated: true });
    } catch (e) {
      console.error('Failed to fetch public settings:', e);
      set({ isLoading: false });
    }
  },

  updateSettings: (newSettings) => {
    const updated = { ...get().settings, ...newSettings };
    // ✅ Always sync to localStorage so other parts of the app (like Login) 
    // can pick up changes immediately on next load/mount
    localStorage.setItem('system_settings', JSON.stringify(updated));
    set({ settings: updated, isHydrated: true });
  },
}));
